import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const SiteSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('campaign');

  const [campaignData, setCampaignData] = useState({
    active: false,
    title: '',
    text: '',
    discount: 0,
    targetAudience: 'all',
    startDate: '',
    endDate: ''
  });

  const [sponsorData, setSponsorData] = useState({
    name: '',
    logo: '',
    url: ''
  });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get('/site-settings/admin');

      if (response.data && response.data.success) {
        const data = response.data.data;
        setSettings(data);
        
        if (data.campaign) {
          setCampaignData({
            active: data.campaign.active || false,
            title: data.campaign.title || '',
            text: data.campaign.text || '',
            discount: data.campaign.discount || 0,
            targetAudience: data.campaign.targetAudience || 'all',
            startDate: data.campaign.startDate ? data.campaign.startDate.split('T')[0] : '',
            endDate: data.campaign.endDate ? data.campaign.endDate.split('T')[0] : ''
          });
        }
      }
    } catch (error) {
      console.error('Settings fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveCampaign = async () => {
    setSaving(true);
    try {
      const response = await axios.put('/site-settings/admin/campaign', {
        campaign: {
          ...campaignData,
          startDate: campaignData.startDate ? new Date(campaignData.startDate).toISOString() : null,
          endDate: campaignData.endDate ? new Date(campaignData.endDate).toISOString() : null
        }
      });

      if (response.data?.success || response.status === 200) {
        alert('Kampanya ayarları kaydedildi!');
        fetchSettings();
      } else {
        throw new Error('Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Campaign save error:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const addSponsor = async () => {
    if (!sponsorData.name) {
      alert('Sponsor adı gerekli');
      return;
    }

    try {
      const response = await axios.post('/site-settings/admin/sponsors', sponsorData);

      if (response.data?.success || response.status === 200) {
        alert('Sponsor eklendi!');
        setSponsorData({ name: '', logo: '', url: '' });
        fetchSettings();
      } else {
        throw new Error('Sponsor eklenemedi');
      }
    } catch (error) {
      console.error('Sponsor add error:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleSponsor = async (index, active) => {
    try {
      const response = await axios.put(`/site-settings/admin/sponsors/${index}`, { active });
      if (response.status === 200) {
        fetchSettings();
      }
    } catch (error) {
      console.error('Sponsor toggle error:', error);
    }
  };

  const deleteSponsor = async (index) => {
    if (!window.confirm('Sponsoru silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await axios.delete(`/site-settings/admin/sponsors/${index}`);
      if (response.status === 200) {
        alert('Sponsor silindi!');
        fetchSettings();
      }
    } catch (error) {
      console.error('Sponsor delete error:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Site Ayarları
          </h3>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('campaign')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'campaign'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Kampanya Yönetimi
              </button>
              <button
                onClick={() => setActiveTab('sponsors')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'sponsors'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Sponsor Yönetimi
              </button>
            </nav>
          </div>

          {/* Campaign Tab */}
          {activeTab === 'campaign' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={campaignData.active}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, active: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Kampanya Aktif</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Kampanya Başlığı</label>
                  <input
                    type="text"
                    value={campaignData.title}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Örn: WebAnt'ta İndirim!"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Kampanya Açıklaması</label>
                  <textarea
                    value={campaignData.text}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, text: e.target.value }))}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Kampanya açıklaması..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">İndirim Oranı (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={campaignData.discount}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, discount: parseInt(e.target.value) || 0 }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Hedef Kitle</label>
                    <select
                      value={campaignData.targetAudience}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, targetAudience: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">Tüm Kullanıcılar</option>
                      <option value="new_users">Yeni Kullanıcılar</option>
                      <option value="existing_users">Mevcut Kullanıcılar</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Başlangıç Tarihi</label>
                    <input
                      type="date"
                      value={campaignData.startDate}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, startDate: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bitiş Tarihi</label>
                    <input
                      type="date"
                      value={campaignData.endDate}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, endDate: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <button
                    onClick={saveCampaign}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                  >
                    {saving ? 'Kaydediliyor...' : 'Kampanya Ayarlarını Kaydet'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sponsors Tab */}
          {activeTab === 'sponsors' && (
            <div className="space-y-6">
              {/* Add Sponsor Form */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-md font-medium text-gray-900 mb-4">Yeni Sponsor Ekle</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sponsor Adı</label>
                    <input
                      type="text"
                      value={sponsorData.name}
                      onChange={(e) => setSponsorData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Sponsor adı"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Logo URL</label>
                    <input
                      type="url"
                      value={sponsorData.logo}
                      onChange={(e) => setSponsorData(prev => ({ ...prev, logo: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website URL</label>
                    <input
                      type="url"
                      value={sponsorData.url}
                      onChange={(e) => setSponsorData(prev => ({ ...prev, url: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://sponsor-website.com"
                    />
                  </div>

                  <div>
                    <button
                      onClick={addSponsor}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Sponsor Ekle
                    </button>
                  </div>
                </div>
              </div>

              {/* Sponsors List */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Mevcut Sponsorlar</h4>
                {settings?.sponsors?.length > 0 ? (
                  <div className="space-y-3">
                    {settings.sponsors.map((sponsor, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-4">
                          {sponsor.logo && (
                            <img src={sponsor.logo} alt={sponsor.name} className="w-12 h-12 object-contain" />
                          )}
                          <div>
                            <h5 className="font-medium text-gray-900">{sponsor.name}</h5>
                            {sponsor.url && (
                              <a href={sponsor.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                                {sponsor.url}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={sponsor.active}
                              onChange={(e) => toggleSponsor(index, e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">Aktif</span>
                          </label>
                          <button
                            onClick={() => deleteSponsor(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Sil
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">Henüz sponsor eklenmemiş.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteSettings;